/**
 * Crypto Utilities - Helper functions for checking crypto API availability and handling fallbacks
 * 
 * Provides crypto API availability checks and MSAL configuration overrides
 * 
 * @author CTS - Ford PM UI Team
 */

/**
 * Check if the Web Crypto API is available in the current environment
 * @returns {boolean} True if crypto API is available, false otherwise
 */
export const isCryptoAvailable = () => {
    try {
        // eslint-disable-next-line no-undef -- Using window for browser compatibility
        return !!(window.crypto?.subtle && window.isSecureContext);   // NOSONAR
    } catch (error) {
        console.warn('Crypto API check failed:', error);
        return false;
    }
};

/**
 * Check if the current context is secure (HTTPS or localhost)
 * @returns {boolean} True if context is secure, false otherwise
 */
export const isSecureContext = () => {
    try {
        // eslint-disable-next-line no-undef -- Using window for browser compatibility
        return window.isSecureContext ||     // NOSONAR
               window.location.protocol === 'https:' ||    // NOSONAR
               window.location.hostname === 'localhost' ||   // NOSONAR
               window.location.hostname === '127.0.0.1';   // NOSONAR

    } catch (error) {
        console.warn('Secure context check failed:', error);
        return false;
    }
};

/**
 * Get recommended MSAL configuration based on crypto availability
 * @returns {object} Configuration overrides for MSAL
 */
export const getMsalConfigOverrides = () => {
    const cryptoAvailable = isCryptoAvailable();
    const secureContext = isSecureContext();
    
    console.log('Crypto API available:', cryptoAvailable);
    console.log('Secure context:', secureContext);
    
    if (!cryptoAvailable || !secureContext) {
        console.warn('Crypto API not available or insecure context detected. Using fallback configuration.');
        return {
            cache: {
                storeAuthStateInCookie: true,
            },
            system: {
                allowNativeBroker: false,
                cryptoOptions: {
                    useMsalCrypto: false,
                    osBrowserCryptoOnly: false
                }
            }
        };
    }
    
    return {};
};

/**
 * Display user-friendly error message for crypto-related issues
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const handleCryptoError = (error) => {
    if (error.message?.includes('crypto_nonexistent')) {
        // eslint-disable-next-line no-undef -- Using window for browser compatibility
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';  // NOSONAR
        
        // eslint-disable-next-line no-undef -- Using window for browser compatibility
        if (!isLocalhost && window.location.protocol !== 'https:') {   // NOSONAR
            return 'Authentication requires a secure connection (HTTPS). Please access the application via HTTPS.';
        }
        
        return 'Your browser does not support the required security features for authentication. Please update your browser or try a different one.';
    }
    
    return error.message || 'An authentication error occurred.';
};

/**
 * Default export for convenience
 */
const cryptoUtils = {
    isCryptoAvailable,
    isSecureContext,
    getMsalConfigOverrides,
    handleCryptoError
};

export default cryptoUtils;
